import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import * as Yup from 'yup'
import React from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { useMutation } from 'react-query'
import toast from 'react-hot-toast'

import { Place, PlaceConfigFlagMap } from '../../types/utils'
import { FILE_SIZE, SUPPORTED_FORMATS } from '../../utils/config'
import { queryClient } from '../../utils/react-query'
import placeService from '../../services/placeService'

import DetailsForm from './DetailsForm'
import { useUser } from '../../hooks/useUser'
import { BuzzlUsersError } from '../../types/buzzl-user'

type DetailsFormType = {
  categories: Array<string>
  customer: string
  address: string
  description: string
  actionText: string
  actionUrl: string
  heroImage?: File
  configFlags: PlaceConfigFlagMap
}

const schema = Yup.object().shape({
  categories: Yup.array(Yup.string()).min(1).required(),
  customer: Yup.string(),
  address: Yup.string(),
  description: Yup.string(),
  actionText: Yup.string(),
  actionUrl: Yup.string().url('Action URL must be a valid URL'),
  configFlags: Yup.object({
    isGeolocationCheckEnabled: Yup.bool(),
  }),
  heroImage: Yup.mixed()
    .test('fileSize', 'File too large', (value) => !value || (value && value.size <= FILE_SIZE))
    .test('fileFormat', 'Unsupported Format', (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))),
})

const DetailsView: React.FC = () => {
  const location = useOutletContext<Place>()
  const user = useUser()
  const updatePlace = useMutation(placeService.updateById, {
    onSuccess: (data) => {
      queryClient.refetchQueries(['location', data.id])
      toast.success('Location Updated.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const initialValues: DetailsFormType = {
    categories: location.categories.map(({ id }) => id),
    customer: location.customer?.name || '',
    address: location.address,
    description: location.description || '',
    actionText: location.actionText || '',
    actionUrl: location.actionUrl || '',
    configFlags: location.configFlags ?? {
      isGeolocationCheckEnabled: true,
    },
    heroImage: undefined,
  }

  const handleOnSubmit = async (values: DetailsFormType, formikBag: FormikHelpers<DetailsFormType>) => {
    let heroImage
    if (values.heroImage) {
      heroImage = await placeService.uploadImage({
        file: values.heroImage,
        customerId: location.customer?.id || 'no-customer',
        placeId: location.id,
      })
    }

    try {
      await updatePlace.mutateAsync({
        id: location.id,
        data: {
          description: values.description,
          actionText: values.actionText,
          actionUrl: values.actionUrl,
          heroImage: heroImage,
          ...(user.data?.type === 'BUZZL' && {
            categories: {
              set: values.categories.map((item) => ({ id: item })),
            },
          }),
          configFlags: values.configFlags,
        },
      })
    } catch (e) {
      console.log(e)
    }
  }

  const generateRatingBadge = () => {
    if (location.averageEvaluation === 'NEGATIVE') {
      return <span className="tag negative-tag">Negative</span>
    } else if (location.averageEvaluation === 'POSITIVE') {
      return <span className="tag positive-tag">Positive</span>
    } else {
      return <span className="tag alert-tag">Neutral</span>
    }
  }
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleOnSubmit}
    >
      {({ isSubmitting, dirty, setFieldValue, values }: FormikProps<DetailsFormType>) => (
        <Form>
          <div className="overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="flex justify-between px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">{location.name}</h3>
              {location.claimStatus === 'CLAIMED' ? (
                <span className="tag positive-tag">Claimed</span>
              ) : (
                <span className="tag positive-tag">Pending</span>
              )}
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <div className="flex items-center p-6 pb-0">
                <span className="mr-6 block text-sm font-medium text-gray-700">Rating</span>
                {generateRatingBadge()}
              </div>
              <DetailsForm
                heroImage={location?.heroImage?.url}
                isSubmitting={isSubmitting}
                locationValidationQrCode={location.locationValidationQrCode}
              />
            </div>
          </div>
          <div className="my-6 flex justify-end space-x-6">
            <Link to="/locations" className="btn btn-white">
              Cancel
            </Link>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting || !dirty}>
              Update
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default DetailsView
